import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../../containers/ApiContainer";
import { useIdentity } from "../../../containers/IdentityContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { AutocompleteAircraft } from "../../Settings/HangarSettings/AutocompleteAircraft";
import { Aircraft } from "../../../types";

type Props = {};

interface TailNumberEntry {
  tail_number: string;
  manufacturer: string;
  model: string;
  created_at?: string;
  added_by_user_id?: string | null;
  users?: {
    email: string;
  } | null;
}

interface FormData {
  tailNumber: string;
  aircraft: Aircraft | null;
}

interface FormErrors {
  tailNumber?: string;
  aircraft?: string;
}

export const TailMappingTab: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const { identity, airplxIdentity } = useIdentity();
  const snackbar = useSnackbar();

  // Form state
  const [formData, setFormData] = React.useState<FormData>({
    tailNumber: "",
    aircraft: null,
  });

  const [errors, setErrors] = React.useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [recentlyAdded, setRecentlyAdded] = React.useState<TailNumberEntry[]>(
    []
  );

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState("");
  const [tailNumberToDelete, setTailNumberToDelete] = React.useState<
    string | null
  >(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Tail number validation
    const tailNumber = formData.tailNumber.trim().toUpperCase();
    if (!tailNumber) {
      newErrors.tailNumber = t('screens.faaMapping.tailMappingTab.form.validation.tailNumberRequired');
    } else if (!/^[A-Z0-9\-]+$/.test(tailNumber)) {
      newErrors.tailNumber = t('screens.faaMapping.tailMappingTab.form.validation.tailNumberFormat');
    } else if (tailNumber.length < 2 || tailNumber.length > 10) {
      newErrors.tailNumber = t('screens.faaMapping.tailMappingTab.form.validation.tailNumberLength');
    }

    // Aircraft validation
    if (!formData.aircraft) {
      newErrors.aircraft = t('screens.faaMapping.tailMappingTab.form.validation.aircraftRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle tail number field change
  const handleTailNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      tailNumber: event.target.value,
    }));

    // Clear error when user types
    setErrors((prev) => ({
      ...prev,
      tailNumber: undefined,
    }));
  };

  // Handle aircraft selection
  const handleAircraftSelect = (aircraft: Aircraft) => {
    setFormData((prev) => ({
      ...prev,
      aircraft,
    }));

    // Clear error when aircraft is selected
    setErrors((prev) => ({
      ...prev,
      aircraft: undefined,
    }));
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    if (!formData.aircraft) {
      return;
    }

    setIsSubmitting(true);

    try {
      const tailNumber = formData.tailNumber.trim().toUpperCase();
      const manufacturer = formData.aircraft.make.toUpperCase();
      const model = formData.aircraft.model.toUpperCase();

      // Check if tail number already exists
      const { data: existingData, error: checkError } = await postgrest
        .from("faa_tail_numbers")
        .select("tail_number")
        .eq("tail_number", tailNumber)
        .single();

      if (existingData && !checkError) {
        snackbar.notify({
          text: t('screens.faaMapping.tailMappingTab.messages.alreadyExists', { tailNumber }),
          severity: "error",
        });
        setIsSubmitting(false);
        return;
      }

      // 1. Insert new tail number with current user ID
      const { data: insertData, error: insertError } = await postgrest
        .from("faa_tail_numbers")
        .insert({
          tail_number: tailNumber,
          manufacturer: manufacturer,
          model: model,
          added_by_user_id: airplxIdentity.userid || null,
        })
        .select("*, users!faa_tail_numbers_added_by_user_id_fkey(email)")
        .single();

      if (insertError) {
        // Handle specific error cases
        if (insertError.code === "23505") {
          snackbar.notify({
            text: t('screens.faaMapping.tailMappingTab.messages.alreadyExists', { tailNumber }),
            severity: "error",
          });
        } else if (insertError.code === "42501") {
          snackbar.notify({
            text: t('screens.faaMapping.tailMappingTab.messages.permissionDenied'),
            severity: "error",
          });
        } else {
          snackbar.notify({
            text: t('screens.faaMapping.tailMappingTab.messages.addFailed', { message: insertError.message }),
            severity: "error",
          });
        }
        setIsSubmitting(false);
        return;
      }

      // 2. Upsert into faa_to_airplx to create the mapping
      // This ensures the tail appears in vw_faa_tail_numbers
      const { error: mappingError } = await postgrest
        .from("faa_to_airplx")
        .upsert(
          {
            faa_manufacturer: manufacturer,
            faa_model: model,
            matched_aircraft_id: formData.aircraft.id,
          },
          { onConflict: "faa_manufacturer,faa_model" }
        );

      if (mappingError) {
        console.error("Error creating faa_to_airplx mapping:", mappingError);
        // Don't fail the whole operation - tail was added, just mapping failed
        snackbar.notify({
          text: t('screens.faaMapping.tailMappingTab.messages.addSuccessNoMapping', { tailNumber }),
          severity: "warning",
        });
      } else {
        snackbar.notify({
          text: t('screens.faaMapping.tailMappingTab.messages.addSuccess', { tailNumber }),
          severity: "success",
        });
      }

      // Add to recently added list with user information
      setRecentlyAdded((prev) => [
        {
          tail_number: tailNumber,
          manufacturer: manufacturer,
          model: model,
          created_at: new Date().toISOString(),
          added_by_user_id: insertData?.added_by_user_id || null,
          users: insertData?.users || null,
        },
        ...prev.slice(0, 9),
      ]);

      // Clear form
      setFormData({
        tailNumber: "",
        aircraft: null,
      });
    } catch (error) {
      console.error("Error adding tail number:", error);
      snackbar.notify({
        text:
          error instanceof Error
            ? t('screens.faaMapping.tailMappingTab.messages.error', { message: error.message })
            : t('screens.faaMapping.tailMappingTab.messages.unexpectedError'),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (tailNumber: string) => {
    setTailNumberToDelete(tailNumber);
    setDeleteDialogOpen(true);
    setDeleteConfirmText("");
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!tailNumberToDelete || deleteConfirmText.toLowerCase() !== "delete") {
      return;
    }

    setIsDeleting(true);

    try {
      const { error } = await postgrest
        .from("faa_tail_numbers")
        .delete()
        .eq("tail_number", tailNumberToDelete);

      if (error) {
        snackbar.notify({
          text: t('screens.faaMapping.tailMappingTab.messages.deleteFailed', { message: error.message }),
          severity: "error",
        });
      } else {
        snackbar.notify({
          text: t('screens.faaMapping.tailMappingTab.messages.deleteSuccess', { tailNumber: tailNumberToDelete }),
          severity: "success",
        });

        // Remove from recently added list
        setRecentlyAdded((prev) =>
          prev.filter((entry) => entry.tail_number !== tailNumberToDelete)
        );
      }
    } catch (error) {
      console.error("Error deleting tail number:", error);
      snackbar.notify({
        text:
          error instanceof Error
            ? t('screens.faaMapping.tailMappingTab.messages.error', { message: error.message })
            : t('screens.faaMapping.tailMappingTab.messages.unexpectedDeleteError'),
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setTailNumberToDelete(null);
      setDeleteConfirmText("");
    }
  };

  // Handle delete dialog close
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTailNumberToDelete(null);
    setDeleteConfirmText("");
  };

  // Load recently added tail numbers on mount
  React.useEffect(() => {
    const loadRecentEntries = async () => {
      try {
        const { data, error } = await postgrest
          .from("faa_tail_numbers")
          .select(
            "tail_number, manufacturer, model, created_at, added_by_user_id, users!faa_tail_numbers_added_by_user_id_fkey(email)"
          )
          .order("created_at", { ascending: false })
          .limit(10);

        if (data && !error) {
          setRecentlyAdded(data);
        }
      } catch (error) {
        console.error("Error loading recent entries:", error);
      }
    };

    loadRecentEntries();
  }, [postgrest]);

  return (
    <Stack direction="column" spacing={3} sx={{ p: 2, height: "100%" }}>
      {/* Header */}
      <Typography variant="h6" color="text.primary">
        {t('screens.faaMapping.tailMappingTab.title')}
      </Typography>

      {/* Form Card */}
      <Card elevation={2}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                {t('screens.faaMapping.tailMappingTab.description')}
              </Typography>

              <Stack direction="row" spacing={2} alignItems="flex-start">
                <TextField
                  label={t('screens.faaMapping.tailMappingTab.form.labels.tailNumber')}
                  placeholder={t('screens.faaMapping.tailMappingTab.form.placeholders.tailNumber')}
                  value={formData.tailNumber}
                  onChange={handleTailNumberChange}
                  error={!!errors.tailNumber}
                  helperText={errors.tailNumber}
                  size="small"
                  sx={{ width: 200 }}
                  disabled={isSubmitting}
                  inputProps={{
                    maxLength: 10,
                    style: { textTransform: "uppercase" },
                  }}
                />

                <Box sx={{ width: 400 }}>
                  <AutocompleteAircraft
                    label={t('screens.faaMapping.tailMappingTab.form.labels.aircraft')}
                    onSelect={handleAircraftSelect}
                    defaultSelected={formData.aircraft || undefined}
                  />
                  {errors.aircraft && (
                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                      {errors.aircraft}
                    </Typography>
                  )}
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    isSubmitting ? <CircularProgress size={20} /> : <AddIcon />
                  }
                  disabled={isSubmitting || !formData.aircraft}
                  sx={{ height: 40, mt: 0.5 }}
                >
                  {isSubmitting ? t('screens.faaMapping.tailMappingTab.form.buttons.adding') : t('screens.faaMapping.tailMappingTab.form.buttons.add')}
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>

      {/* Recently Added Section */}
      <Paper
        elevation={1}
        sx={{
          flexGrow: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {t('screens.faaMapping.tailMappingTab.recentlyAdded.title')}
          </Typography>
        </Box>

        <TableContainer sx={{ flexGrow: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  {t('screens.faaMapping.tailMappingTab.recentlyAdded.columns.tailNumber')}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  {t('screens.faaMapping.tailMappingTab.recentlyAdded.columns.manufacturer')}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  {t('screens.faaMapping.tailMappingTab.recentlyAdded.columns.model')}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  {t('screens.faaMapping.tailMappingTab.recentlyAdded.columns.source')}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                  }}
                >
                  {t('screens.faaMapping.tailMappingTab.recentlyAdded.columns.added')}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                    width: 80,
                  }}
                >
                  {t('screens.faaMapping.tailMappingTab.recentlyAdded.columns.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentlyAdded.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('screens.faaMapping.tailMappingTab.recentlyAdded.emptyState')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recentlyAdded.map((entry) => (
                  <TableRow key={entry.tail_number}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {entry.tail_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{entry.manufacturer}</TableCell>
                    <TableCell>{entry.model}</TableCell>
                    <TableCell>
                      {entry.added_by_user_id && entry.users?.email ? (
                        <Typography variant="body2" color="text.primary">
                          {entry.users.email}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          {t('screens.faaMapping.tailMappingTab.recentlyAdded.sources.faaImport')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.created_at
                        ? new Date(entry.created_at).toLocaleString("en-US", {
                            month: "numeric",
                            day: "numeric",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                        : t('screens.faaMapping.tailMappingTab.recentlyAdded.sources.justNow')}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(entry.tail_number)}
                        sx={{ color: "error.main" }}
                        title={t('screens.faaMapping.tailMappingTab.tooltips.delete', { tailNumber: entry.tail_number })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('screens.faaMapping.tailMappingTab.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ mb: 2 }}
            dangerouslySetInnerHTML={{
              __html: t('screens.faaMapping.tailMappingTab.deleteDialog.confirmationText', {
                tailNumber: tailNumberToDelete
              })
            }}
          />
          <DialogContentText
            sx={{ mb: 2 }}
            dangerouslySetInnerHTML={{
              __html: t('screens.faaMapping.tailMappingTab.deleteDialog.instructionsText')
            }}
          />
          <TextField
            fullWidth
            placeholder={t('screens.faaMapping.tailMappingTab.deleteDialog.placeholder')}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            size="small"
            error={
              deleteConfirmText !== "" &&
              deleteConfirmText.toLowerCase() !== "delete"
            }
            helperText={
              deleteConfirmText !== "" &&
              deleteConfirmText.toLowerCase() !== "delete"
                ? t('screens.faaMapping.tailMappingTab.deleteDialog.validationError')
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            {t('screens.faaMapping.tailMappingTab.deleteDialog.buttons.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={
              isDeleting || deleteConfirmText.toLowerCase() !== "delete"
            }
            startIcon={
              isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {isDeleting ? t('screens.faaMapping.tailMappingTab.deleteDialog.buttons.deleting') : t('screens.faaMapping.tailMappingTab.deleteDialog.buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
